import { useState } from 'react'
import { catalogApi } from '../lib/api'
import type { Category, Product } from '../lib/db'
import { useApp } from '../lib/store'
import { Sheet } from './Sheet'

interface Props {
  categories: Category[]
  products: Product[]
  onClose: () => void
  onChanged: () => void | Promise<void>
}

type Pending = { category: Category; count: number } | null

export function CategoryManager({ categories, products, onClose, onChanged }: Props) {
  const say = useApp((s) => s.say)

  const [order, setOrder] = useState<Category[]>(categories)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draftName, setDraftName] = useState('')
  const [adding, setAdding] = useState('')
  const [deleting, setDeleting] = useState<Pending>(null)
  const [moveTo, setMoveTo] = useState<string>('')
  const [busy, setBusy] = useState(false)

  const countIn = (id: string) => products.filter((p) => p.categoryId === id).length

  async function rename(c: Category) {
    const name = draftName.trim()
    setEditingId(null)
    if (!name || name === c.name) return

    setBusy(true)
    try {
      await catalogApi.saveCategory({ id: c.id, name })
      setOrder((list) => list.map((x) => (x.id === c.id ? { ...x, name } : x)))
      await onChanged()
      say(`Đã đổi tên thành ${name}`)
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa đổi được tên')
    }
    setBusy(false)
  }

  async function add() {
    const name = adding.trim()
    if (!name) return

    setBusy(true)
    try {
      await catalogApi.saveCategory({ name })
      setAdding('')
      await onChanged()
      say(`Đã thêm nhóm ${name}`)
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa thêm được nhóm')
    }
    setBusy(false)
  }

  /** Đổi chỗ hai nhóm rồi lưu cả danh sách một lượt — thứ tự này là thứ tự tab lúc gọi món. */
  async function move(index: number, delta: number) {
    const next = [...order]
    const target = index + delta
    if (target < 0 || target >= next.length) return

    const a = next[index]
    const b = next[target]
    if (!a || !b) return

    next[index] = b
    next[target] = a
    setOrder(next)

    try {
      await catalogApi.reorderCategories(next.map((c) => c.id))
      await onChanged()
    } catch {
      setOrder(order)
      say('Cần có mạng để đổi thứ tự nhóm')
    }
  }

  async function confirmDelete() {
    if (!deleting) return
    setBusy(true)
    try {
      // moveTo rỗng nghĩa là đẩy món về "chưa xếp nhóm" — vẫn bán được, chỉ là chưa xếp.
      await catalogApi.deleteCategory(deleting.category.id, moveTo || undefined)
      setOrder((list) => list.filter((c) => c.id !== deleting.category.id))
      setDeleting(null)
      await onChanged()
      say(
        deleting.count > 0
          ? `Đã xoá nhóm, ${deleting.count} món chuyển sang nhóm khác`
          : 'Đã xoá nhóm',
      )
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa xoá được nhóm')
    }
    setBusy(false)
  }

  if (deleting) {
    const others = order.filter((c) => c.id !== deleting.category.id)
    return (
      <Sheet title={`Xoá nhóm ${deleting.category.name}`} onClose={() => setDeleting(null)}>
        {deleting.count === 0 ? (
          <p className="sheet-note">Nhóm này chưa có món nào. Xoá là xong.</p>
        ) : (
          <>
            <p className="sheet-note">
              Nhóm này còn <b>{deleting.count} món</b>. Món không bị xoá — chọn xem chúng đi đâu.
            </p>
            <label className="field">
              <span>Chuyển {deleting.count} món sang</span>
              <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                <option value="">Chưa xếp nhóm</option>
                {others.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        <div className="sheet-actions">
          <button type="button" className="btn btn-ghost" onClick={() => setDeleting(null)}>
            Quay lại
          </button>
          <button
            type="button"
            className="btn btn-danger-ghost grow"
            onClick={confirmDelete}
            disabled={busy}
          >
            {busy ? 'Đang xoá…' : 'Xoá nhóm'}
          </button>
        </div>
      </Sheet>
    )
  }

  return (
    <Sheet title="Nhóm món" onClose={onClose}>
      <p className="sheet-note">
        Thứ tự ở đây là thứ tự tab lúc gọi món — nhóm hay bán thì đẩy lên trên.
      </p>

      <div className="list">
        {order.map((c, i) => {
          const n = countIn(c.id)
          return (
            <div key={c.id} className="group-row">
              <div className="group-move">
                <button
                  type="button"
                  onClick={() => void move(i, -1)}
                  disabled={i === 0}
                  aria-label={`Đưa ${c.name} lên trên`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => void move(i, 1)}
                  disabled={i === order.length - 1}
                  aria-label={`Đưa ${c.name} xuống dưới`}
                >
                  ↓
                </button>
              </div>

              {editingId === c.id ? (
                <input
                  className="group-input"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onBlur={() => void rename(c)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void rename(c)
                    if (e.key === 'Escape') setEditingId(null)
                  }}
                  maxLength={80}
                  autoFocus
                />
              ) : (
                <button
                  type="button"
                  className="group-name"
                  onClick={() => {
                    setDraftName(c.name)
                    setEditingId(c.id)
                  }}
                >
                  <b>{c.name}</b>
                  <small>{n > 0 ? `${n} món` : 'chưa có món'}</small>
                </button>
              )}

              <button
                type="button"
                className="group-del"
                onClick={() => {
                  setMoveTo('')
                  setDeleting({ category: c, count: n })
                }}
                aria-label={`Xoá nhóm ${c.name}`}
              >
                🗑
              </button>
            </div>
          )
        })}
      </div>

      <label className="field">
        <span>Thêm nhóm mới</span>
        <input
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          placeholder="Đồ ăn vặt"
          maxLength={80}
          onKeyDown={(e) => e.key === 'Enter' && void add()}
        />
      </label>

      <div className="sheet-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Đóng
        </button>
        <button
          type="button"
          className="btn btn-brand grow"
          onClick={add}
          disabled={busy || !adding.trim()}
        >
          Thêm nhóm
        </button>
      </div>
    </Sheet>
  )
}
